<script setup lang="ts">
/*
 * Console bar — the only top chrome. Logo · segmented ConsoleTabs in the
 * middle · round buttons on the right (search, wishlist, cart, account).
 * No inline search box, no category band: search is a full-screen overlay
 * (⌘K / `/` / the button), categories are tiles on Home and the Browse
 * filter rail. Below `md` the tabs move to the bottom tab bar.
 */
import { Heart, Menu, Search, ShoppingCart } from 'lucide-vue-next';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import logoUrl from '../assets/logo.svg';
import { useAuthStore } from '../stores/auth';
import { useCartStore } from '../stores/cart';
import { useFavoritesStore } from '../stores/favorites';
import { useUiStore } from '../stores/ui';
import AccountMenu from './shell/AccountMenu.vue';
import ConsoleTabs from './shell/ConsoleTabs.vue';
import CountBadge from './shell/CountBadge.vue';

const { t } = useI18n();
const auth = useAuthStore();
const cart = useCartStore();
const favorites = useFavoritesStore();
const ui = useUiStore();
</script>

<template>
  <header class="sticky top-0 z-40 border-b border-border/60 bg-bg-base/85 backdrop-blur-md">
    <div class="mx-auto flex h-[72px] max-w-page items-center gap-3 px-4 md:px-8">
      <button type="button" class="btn-icon md:hidden" :aria-label="t('header.aria.menu')" @click="ui.openMobileNav()">
        <Menu class="h-5 w-5" aria-hidden="true" />
      </button>

      <RouterLink to="/" class="flex shrink-0 items-center rounded-full transition-transform hover:scale-[1.03] active:scale-[0.98]" :aria-label="t('header.aria.home')">
        <!-- logo.svg has no intrinsic size: 178×30.5 viewBox, so width is set here. -->
        <img :src="logoUrl" alt="KeyzoStore" width="163" height="28" class="h-7 w-[163px] md:h-8 md:w-[187px]" />
      </RouterLink>

      <div class="flex flex-1 justify-center">
        <ConsoleTabs />
      </div>

      <div class="flex shrink-0 items-center gap-2">
        <button type="button" class="btn-icon" :aria-label="t('header.aria.search')" @click="ui.openPalette()">
          <Search class="h-5 w-5" aria-hidden="true" />
        </button>
        <RouterLink to="/favorites" class="btn-icon relative hidden sm:inline-flex" :aria-label="t('header.aria.favorites')">
          <Heart class="h-5 w-5" aria-hidden="true" />
          <CountBadge :count="favorites.count" />
        </RouterLink>
        <button type="button" data-cart-target class="btn-icon relative" :aria-label="t('header.aria.cart', { count: cart.count })" @click="ui.openMiniCart()">
          <ShoppingCart class="h-5 w-5" aria-hidden="true" />
          <CountBadge :count="cart.count" />
        </button>
        <AccountMenu v-if="auth.isAuthenticated" />
        <template v-else>
          <RouterLink to="/login" class="btn-ghost hidden md:inline-flex">{{ t('common.actions.signIn') }}</RouterLink>
          <RouterLink to="/register" class="btn-secondary btn-sm hidden md:inline-flex">{{ t('common.actions.signUp') }}</RouterLink>
        </template>
      </div>
    </div>
  </header>
</template>
